import type { CSSProperties, Dispatch, SetStateAction } from 'react';
import {
  PUBLIC_DESIGN_MODES,
  designPreview,
  designModeLabels,
  chamferClipPath,
  themeCornerIsChamfer,
  type DesignMode,
} from '../config/theme';
import { useTheme } from '../context/ThemeContext';

type Theme = ReturnType<typeof useTheme>;

type ThemeSelectorProps = {
  designMode: DesignMode;
  setDesignMode: (m: DesignMode) => void;
  hoverMode: number | null;
  setHoverMode: Dispatch<SetStateAction<number | null>>;
  modes?: DesignMode[];
  layout?: 'inline' | 'stack';
  showLabel?: boolean;
  theme?: Theme;
};

export function ThemeSelector({
  designMode,
  setDesignMode,
  hoverMode,
  setHoverMode,
  modes = PUBLIC_DESIGN_MODES,
  layout = 'inline',
  showLabel = true,
  theme: themeProp,
}: ThemeSelectorProps) {
  const contextTheme = useTheme();
  const theme = themeProp ?? contextTheme;
  const isStack = layout === 'stack';
  const chamferMenu = themeCornerIsChamfer(theme);
  const trayClip = chamferMenu ? chamferClipPath(theme.chamferCutMicro ?? '3px') : undefined;
  const trayShape: CSSProperties = chamferMenu
    ? { borderRadius: 0, clipPath: trayClip, WebkitClipPath: trayClip }
    : { borderRadius: '0.5rem' };
  const modeSwatchClip = chamferMenu ? chamferClipPath(theme.chamferCutMicro ?? '3px') : undefined;
  const swatchSize = isStack ? '1.125rem' : '1.25rem';
  const activeScale = isStack ? '1.12' : '1.15';

  const wrapperStyle: CSSProperties = isStack
    ? {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap',
      }
    : { display: 'flex', alignItems: 'center', gap: '0.5rem' };

  return (
    <div style={wrapperStyle}>
      {showLabel && (
        <span
          style={{
            fontSize: isStack ? '0.625rem' : '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            opacity: isStack ? 0.85 : 0.8,
            color: theme.textMuted,
          }}
        >
          Theme
        </span>
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          justifyContent: 'center',
          gap: '0.125rem',
          padding: '0.125rem',
          backgroundColor:
            typeof theme.bg === 'string' && !theme.bg.includes('gradient')
              ? theme.bg
              : 'rgba(128,128,128,0.08)',
          ...trayShape,
          border: `1px solid ${theme.border}`,
        }}
        role="group"
        aria-label="Theme selector"
      >
        {modes.map((mode) => {
          const isActive = designMode === mode;
          const isHover = hoverMode === mode && !isActive;
          const preview = designPreview[mode];
          const labels = designModeLabels[mode];
          const diagonalBg = `linear-gradient(135deg, ${preview.color1} 0% 50%, ${preview.color2} 50% 100%)`;
          const modeSwatchShape: CSSProperties = chamferMenu
            ? { borderRadius: 0, clipPath: modeSwatchClip, WebkitClipPath: modeSwatchClip }
            : { borderRadius: preview.borderRadius };

          return (
            <button
              key={mode}
              type="button"
              onClick={() => setDesignMode(mode)}
              onMouseEnter={() => setHoverMode(mode)}
              onMouseLeave={() => setHoverMode(null)}
              aria-label={labels.ariaLabel}
              title={labels.title}
              style={{
                width: swatchSize,
                height: swatchSize,
                padding: 0,
                border: 'none',
                cursor: 'pointer',
                background: diagonalBg,
                opacity: isActive ? 1 : isHover ? 0.9 : 0.6,
                transition: 'opacity 0.2s ease, transform 0.15s ease',
                transform: isActive ? `scale(${activeScale})` : 'scale(1)',
                boxShadow: isActive ? `0 0 0 6px ${theme.textMuted}` : 'none',
                ...modeSwatchShape,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
